import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, Popup, useMap, Circle } from 'react-leaflet';
import L from 'leaflet';
import { motion } from 'framer-motion';
import { 
  MapPin, Navigation, Filter, Star, Clock, Users, 
  Loader2, RefreshCw, X, ChevronRight, Wifi, WifiOff,
  Mountain, PartyPopper, Utensils, Landmark, Leaf, Dumbbell, HeartPulse, Moon, Info
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { mapApi } from '@/api';
import { useAuth } from '@/context/AuthContext';
import { StarRating } from '@/components/StarRating';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons
const createCustomIcon = (isAvailable, isSelected = false) => {
  const color = isAvailable ? '#f97316' : '#9ca3af';
  const size = isSelected ? 50 : 40;
  
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        background: ${color};
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        border: 3px solid white;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <div style="
          transform: rotate(45deg);
          color: white;
          font-size: ${size * 0.4}px;
          font-weight: bold;
        ">
          ${isAvailable ? '●' : '○'}
        </div>
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size]
  });
};

const userLocationIcon = L.divIcon({
  className: 'user-location-marker',
  html: `
    <div style="
      width: 20px;
      height: 20px;
      background: #3b82f6;
      border-radius: 50%;
      border: 4px solid white;
      box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.3), 0 4px 12px rgba(0,0,0,0.3);
    "></div>
  `,
  iconSize: [20, 20],
  iconAnchor: [10, 10]
});

// Map controller component
const MapController = ({ center, zoom }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center) {
      map.setView(center, zoom || 12);
    }
  }, [center, zoom, map]);
  
  return null;
};

const CATEGORY_ICONS = {
  adventure: Mountain,
  fun: PartyPopper,
  food: Utensils,
  culture: Landmark,
  nature: Leaf,
  sport: Dumbbell,
  healing: HeartPulse,
  nightlife: Moon,
  local_events: PartyPopper  // Same icon as fun for local events
};

const MapPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [explorers, setExplorers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [selectedExplorer, setSelectedExplorer] = useState(null);
  const [showOnlyAvailable, setShowOnlyAvailable] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [mapCenter, setMapCenter] = useState([46.2276, 2.2137]); // France center
  const [mapZoom, setMapZoom] = useState(6);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchRef = useRef(null);

  const categories = [
    { id: 'adventure', label: i18n.language === 'fr' ? 'Aventure & Nature' : 'Adventure & Nature' },
    { id: 'fun', label: i18n.language === 'fr' ? 'Fun' : 'Fun' },
    { id: 'food', label: i18n.language === 'fr' ? 'Gastronomie' : 'Food' },
    { id: 'culture', label: i18n.language === 'fr' ? 'Culture' : 'Culture' },
    { id: 'healing', label: i18n.language === 'fr' ? 'Healing' : 'Healing' },
    { id: 'sport', label: i18n.language === 'fr' ? 'Sport' : 'Sports' },
    { id: 'local_events', label: i18n.language === 'fr' ? 'Événements locaux' : 'Local Events' }
  ];

  // Get user location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(loc);
          setMapCenter([loc.lat, loc.lng]);
          setMapZoom(10);
        },
        (error) => {
          console.log('Geolocation error:', error);
        }
      );
    }
  }, []);

  // Fetch explorers
  const fetchExplorers = async () => {
    setLoading(true);
    try {
      let data;
      if (showOnlyAvailable) {
        const params = {};
        if (userLocation) {
          params.lat = userLocation.lat;
          params.lng = userLocation.lng;
          params.radius_km = 100;
        }
        if (selectedCategory) {
          params.category = selectedCategory;
        }
        data = await mapApi.getAvailableExplorers(params);
      } else {
        data = await mapApi.getAllExplorers();
      }
      setExplorers(data);
    } catch (error) {
      console.error('Error fetching explorers:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExplorers();
  }, [showOnlyAvailable, selectedCategory, userLocation]);

  const handleExplorerClick = (explorer) => {
    setSelectedExplorer(explorer);
    setMapCenter([explorer.latitude, explorer.longitude]);
    setMapZoom(14);
  };

  // Search location with autocomplete using Nominatim (OpenStreetMap)
  const searchLocation = async (query) => {
    if (!query || query.length < 2) {
      setSearchSuggestions([]);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&countrycodes=fr,ch,be,lu,mc&accept-language=${i18n.language}`
      );
      const data = await response.json();
      
      setSearchSuggestions(data.map(item => ({
        name: item.display_name,
        lat: parseFloat(item.lat),
        lng: parseFloat(item.lon),
        type: item.type,
        importance: item.importance
      })));
      setShowSuggestions(true);
    } catch (error) {
      console.error('Search error:', error);
      setSearchSuggestions([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) {
        searchLocation(searchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectLocation = (suggestion) => {
    setMapCenter([suggestion.lat, suggestion.lng]);
    setMapZoom(12);
    setSearchQuery(suggestion.name.split(',')[0]);
    setShowSuggestions(false);
  };

  const handleCenterOnUser = () => {
    if (userLocation) {
      setMapCenter([userLocation.lat, userLocation.lng]);
      setMapZoom(12);
    }
  };

  return (
    <main className="min-h-screen pt-16" data-testid="map-page">
      <div className="flex h-[calc(100vh-64px)]">
        {/* Sidebar */}
        <motion.div
          initial={{ x: -300 }}
          animate={{ x: 0 }}
          className="w-96 bg-white border-r shadow-lg overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="p-4 border-b bg-gradient-to-r from-primary to-orange-500">
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              {i18n.language === 'fr' ? 'Carte des Guides' : 'Guides Map'}
            </h1>
            <p className="text-white/80 text-sm mt-1">
              {explorers.length} {i18n.language === 'fr' 
                ? (showOnlyAvailable ? 'guides disponibles' : 'guides sur la carte')
                : (showOnlyAvailable ? 'available guides' : 'guides on map')}
            </p>
          </div>

          {/* Demo Banner */}
          <div className="bg-amber-50 border-b border-amber-200 py-2 px-3">
            <div className="flex items-center gap-2 text-amber-700">
              <Info className="w-4 h-4 flex-shrink-0" />
              <p className="text-xs font-medium">
                {i18n.language === 'fr' 
                  ? 'Profils de démonstration' 
                  : 'Demo profiles'}
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="p-4 border-b space-y-3">
            {/* Search Bar with Autocomplete */}
            <div ref={searchRef} className="relative">
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => searchQuery.length >= 2 && setShowSuggestions(true)}
                  placeholder={i18n.language === 'fr' ? 'Rechercher une ville, région...' : 'Search a city, region...'}
                  className="w-full pl-10 pr-10 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="map-search-input"
                />
                {searchLoading && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
                )}
                {searchQuery && !searchLoading && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setSearchSuggestions([]);
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                  >
                    <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  </button>
                )}
              </div>
              
              {/* Suggestions dropdown */}
              {showSuggestions && searchSuggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-50 max-h-60 overflow-auto">
                  {searchSuggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => handleSelectLocation(suggestion)}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b last:border-b-0 flex items-start gap-3"
                    >
                      <MapPin className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {suggestion.name.split(',')[0]}
                        </p>
                        <p className="text-xs text-gray-500 line-clamp-1">
                          {suggestion.name.split(',').slice(1).join(',')}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              
              {/* No results message */}
              {showSuggestions && searchQuery.length >= 2 && searchSuggestions.length === 0 && !searchLoading && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-50 p-4 text-center text-sm text-gray-500">
                  {i18n.language === 'fr' ? 'Aucun résultat trouvé' : 'No results found'}
                </div>
              )}
            </div>

            {/* Available toggle */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium flex items-center gap-2">
                {showOnlyAvailable ? <Wifi className="w-4 h-4 text-green-500" /> : <WifiOff className="w-4 h-4 text-gray-400" />}
                {i18n.language === 'fr' ? 'Disponibles maintenant' : 'Available now'}
              </span>
              <button
                onClick={() => setShowOnlyAvailable(!showOnlyAvailable)}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  showOnlyAvailable ? 'bg-primary' : 'bg-gray-300'
                }`}
              >
                <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${
                  showOnlyAvailable ? 'left-7' : 'left-1'
                }`} />
              </button>
            </div>

            {/* Category filter */}
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant={selectedCategory === null ? 'default' : 'outline'}
                onClick={() => setSelectedCategory(null)}
                className="rounded-full text-xs"
              >
                {i18n.language === 'fr' ? 'Tous' : 'All'}
              </Button>
              {categories.map((cat) => {
                const Icon = CATEGORY_ICONS[cat.id] || MapPin; // Fallback to MapPin if icon not found
                return (
                  <Button
                    key={cat.id}
                    size="sm"
                    variant={selectedCategory === cat.id ? 'default' : 'outline'}
                    onClick={() => setSelectedCategory(cat.id)}
                    className="rounded-full text-xs"
                  >
                    <Icon className="w-3 h-3 mr-1" />
                    {cat.label}
                  </Button>
                );
              })}
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleCenterOnUser}
                disabled={!userLocation}
                className="flex-1"
              >
                <Navigation className="w-4 h-4 mr-1" />
                {i18n.language === 'fr' ? 'Ma position' : 'My location'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={fetchExplorers}
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>

          {/* Explorer List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : explorers.length === 0 ? (
              <div className="text-center py-12 px-4">
                <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">
                  {i18n.language === 'fr' 
                    ? 'Aucun guide trouvé dans cette zone'
                    : 'No guides found in this area'}
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {explorers.map((explorer) => (
                  <motion.div
                    key={explorer.user_id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className={`p-4 cursor-pointer hover:bg-orange-50 transition-colors ${
                      selectedExplorer?.user_id === explorer.user_id ? 'bg-orange-50 border-l-4 border-primary' : ''
                    }`}
                    onClick={() => handleExplorerClick(explorer)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="relative">
                        <img 
                          src={explorer.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(explorer.name)}&background=f97316&color=fff&size=48`}
                          alt={explorer.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                        {explorer.is_available_now && (
                          <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-gray-900 truncate">{explorer.name}</h3>
                          {explorer.rating > 0 && (
                            <div className="flex items-center gap-1 text-sm">
                              <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                              <span>{explorer.rating.toFixed(1)}</span>
                            </div>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 truncate">{explorer.location}</p>
                        {explorer.distance_km !== undefined && (
                          <p className="text-xs text-primary font-medium mt-1">
                            {explorer.distance_km} km
                          </p>
                        )}
                        {explorer.specialties && explorer.specialties.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {explorer.specialties.slice(0, 3).map((spec) => {
                              const Icon = CATEGORY_ICONS[spec];
                              return Icon ? (
                                <span key={spec} className="w-5 h-5 rounded-full bg-orange-100 flex items-center justify-center">
                                  <Icon className="w-3 h-3 text-primary" />
                                </span>
                              ) : null;
                            })}
                          </div>
                        )}
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </motion.div>

        {/* Map */}
        <div className="flex-1 relative">
          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            className="w-full h-full"
            zoomControl={false}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            <MapController center={mapCenter} zoom={mapZoom} />

            {/* User location */}
            {userLocation && (
              <>
                <Marker position={[userLocation.lat, userLocation.lng]} icon={userLocationIcon}>
                  <Popup>
                    <span className="font-medium">
                      {i18n.language === 'fr' ? 'Votre position' : 'Your location'}
                    </span>
                  </Popup>
                </Marker>
                <Circle 
                  center={[userLocation.lat, userLocation.lng]}
                  radius={50000}
                  pathOptions={{ color: '#3b82f6', fillColor: '#3b82f6', fillOpacity: 0.1 }}
                />
              </>
            )}

            {/* Explorer markers */}
            {explorers.map((explorer) => (
              <Marker
                key={explorer.user_id}
                position={[explorer.latitude, explorer.longitude]}
                icon={createCustomIcon(explorer.is_available_now, selectedExplorer?.user_id === explorer.user_id)}
                eventHandlers={{
                  click: () => handleExplorerClick(explorer)
                }}
              >
                <Popup>
                  <div className="min-w-[200px]">
                    <div className="flex items-center gap-3 mb-2">
                      <img 
                        src={explorer.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(explorer.name)}&background=f97316&color=fff&size=40`}
                        alt={explorer.name}
                        className="w-10 h-10 rounded-full"
                      />
                      <div>
                        <h4 className="font-semibold">{explorer.name}</h4>
                        <p className="text-xs text-gray-500">{explorer.location}</p>
                      </div>
                    </div>
                    {explorer.rating > 0 && (
                      <div className="mb-2">
                        <StarRating rating={explorer.rating} size="sm" />
                      </div>
                    )}
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        explorer.is_available_now 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {explorer.is_available_now 
                          ? (i18n.language === 'fr' ? 'Disponible' : 'Available')
                          : (i18n.language === 'fr' ? 'Hors ligne' : 'Offline')}
                      </span>
                    </div>
                    <Link to={`/explorer/${explorer.user_id}`}>
                      <Button size="sm" className="w-full">
                        {i18n.language === 'fr' ? 'Voir le profil' : 'View profile'}
                      </Button>
                    </Link>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>

          {/* Legend */}
          <div className="absolute bottom-4 right-4 bg-white rounded-lg shadow-lg p-3 z-[1000]">
            <h4 className="text-xs font-semibold mb-2 text-gray-600">
              {i18n.language === 'fr' ? 'Légende' : 'Legend'}
            </h4>
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-primary" />
                <span>{i18n.language === 'fr' ? 'Disponible' : 'Available'}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-gray-400" />
                <span>{i18n.language === 'fr' ? 'Hors ligne' : 'Offline'}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-blue-500" />
                <span>{i18n.language === 'fr' ? 'Vous' : 'You'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default MapPage;
